<template>
  <section class="edit-ticket-content__scrollable">
    <h5
      v-if="store.getters.hasErrandEditorAccess && selectedTicket?.active"
      class="edit-ticket-content__log__heading"
    >
      {{ t('general__text__log') }}
    </h5>
    <TextAreaButton
      v-if="store.getters.hasErrandEditorAccess && selectedTicket?.active"
      ref="logTextAreaRef"
      :buttonText="t('general__text__update') + ' ' + t('general__data__ticket-the').toLowerCase()"
      :placeHolderText="t('general__data__ticket-add-to-log')"
      :disabled="!isLogValueAdded"
      style="margin-bottom: 32px"
      @emitInput="handleLogInput"
      @onClicked="createLog"
    />
    <h5 class="edit-ticket-content__log__heading">{{ t('general__text__history') }}</h5>
    <PulseLoader :loading="isLoadingHistory" class="spinner" color="#1d234f" />
    <article v-for="item in historyItems" :key="item.id">
      <div class="edit-ticket-content__log__title">
        <p>{{ getTitle(item.type) }}</p>
        <p>{{ formatTitleDate(item.createdDate) }}</p>
      </div>
      <div v-if="item.type === 'log'" class="edit-ticket-content__log__body">
        <p class="edit-ticket-content__log__body__text">{{ item.value.to }}</p>
      </div>
      <div
        v-if="
          item.type === 'assignee' ||
          item.type === 'dueDate' ||
          item.type === 'externalId' ||
          item.type === 'finishedDate' ||
          item.type === 'startDate' ||
          item.type === 'title'
        "
        class="edit-ticket-content__log__body"
      >
        <p class="edit-ticket-content__log__body__text">
          {{ getFormattedValue(item.type, item.value.from) }}
        </p>
        <IconArrowNarrowRight class="edit-ticket-content__log__body__arrow" />
        <p class="edit-ticket-content__log__body__text">
          {{ getFormattedValue(item.type, item.value.to) }}
        </p>
      </div>
      <div
        v-if="
          item.type === 'categoryId' ||
          item.type === 'priority' ||
          item.type === 'status' ||
          item.type === 'typeId'
        "
        class="edit-ticket-content__log__body"
      >
        <BadgeSquare
          v-if="item.value.from?.length"
          :key="item.id"
          :badgeText="getFormattedValue(item.type, item.value.from)"
          :customColor="getCustomColor(item.type, item.value.from)"
          size="small"
        />
        <p v-else class="edit-ticket-content__log__body__text">
          {{ getFormattedValue(item.type, item.value.from) }}
        </p>
        <IconArrowNarrowRight class="edit-ticket-content__log__body__arrow" />
        <BadgeSquare
          :key="item.id"
          :badgeText="getFormattedValue(item.type, item.value.to)"
          :customColor="getCustomColor(item.type, item.value.to)"
          size="small"
        />
      </div>
      <div
        v-if="item.type === 'description'"
        class="edit-ticket-content__log__body edit-ticket-content__log__body__description"
      >
        <p class="edit-ticket-content__log__body__description__text">
          {{ getFormattedValue(item.type, item.value.from) }}
        </p>
        <IconArrowNarrowRight class="edit-ticket-content__log__body__arrow" />
        <p class="edit-ticket-content__log__body__description__text">
          {{ getFormattedValue(item.type, item.value.to) }}
        </p>
      </div>
      <DividerHorizontal style="margin: 16px 0" />
    </article>
    <article v-if="!isLoadingHistory">
      <div class="edit-ticket-content__log__title">
        <p>
          {{ t('general__data__ticket-the') }} {{ t('general__text__created-was').toLowerCase() }}
        </p>
        <p>
          {{ selectedTicket?.createdDate ? formatTitleDate(selectedTicket.createdDate) : '' }}
        </p>
      </div>
    </article>
  </section>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import {
  createComment,
  getTicketCategories,
  getTicketCommentsPage,
  getTicketHistoryPage,
  getTicketTypesForCategory,
  Page,
  TicketCategory,
  TicketPatch,
  TicketType
} from '@/services/tickets.service'
import {
  getPriorityColorLight,
  getStatusColorLight,
  translatedPriority,
  translatedStatus
} from '@/utils/ticketHelpers'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import IconArrowNarrowRight from '@/design-system/assets/icons/IconArrowNarrowRight.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TextAreaButton from '@/design-system/atoms/TextAreaButton.vue'
import * as Sentry from '@sentry/vue'

type TicketLogItem = {
  createdDate: string
  id: string
  type:
    | 'assignee'
    | 'categoryId'
    | 'description'
    | 'dueDate'
    | 'externalId'
    | 'finishedDate'
    | 'log' // From separate "comments" endpoint
    | 'priority'
    | 'startDate'
    | 'status'
    | 'title'
    | 'typeId'
  value: {
    from: string | null | undefined
    to: string | null | undefined
  }
}

const { locale, t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

const historyItems = ref<TicketLogItem[]>([])
const isLoadingHistory = ref(false)
const isLogValueAdded = ref(false)
const logTextAreaRef = ref()
const ticketCategories = ref<TicketCategory[]>([])
const ticketTypes = ref<TicketType[]>([])

const selectedTicket = computed(() => piniaStore.selectedTicket)

async function createLog(value: string | number) {
  if (!selectedTicket.value) return
  if (value.toString().length) {
    try {
      const comment = await createComment(selectedTicket.value.id, { comment: value.toString() })
      if (comment) {
        logTextAreaRef.value.clearInputValue()
        isLogValueAdded.value = false
        reloadHistory()
      } else {
        store.commit('setInfoMessage', {
          type: 'error',
          message: `${t('general__data__log-create-failed')}`
        })
      }
    } catch (error) {
      Sentry.captureException(error)
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${t('general__data__log-create-failed')}`
      })
    }
  }
}
function handleLogInput(value: string | number) {
  if (value.toString().length) isLogValueAdded.value = true
  else isLogValueAdded.value = false
}
function formatTitleDate(date: string) {
  let dateString = new Date(date).toLocaleTimeString(locale.value, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
  if (locale.value === 'sv') {
    dateString = dateString.replace(' ', ' kl. ')
  }
  return dateString
}
function generateHistoryItems(historyContent: TicketPatch[]): TicketLogItem[] {
  const historyArray: TicketLogItem[] = []
  historyContent.forEach((historyItem) => {
    const createdDate = historyItem.createdDate ?? ''
    if (historyItem.assignee) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_assignee',
        type: 'assignee',
        value: { from: historyItem.assignee.from, to: historyItem.assignee.to }
      })
    }
    if (historyItem.categoryId) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_categoryId',
        type: 'categoryId',
        value: { from: historyItem.categoryId.from, to: historyItem.categoryId.to }
      })
    }
    if (historyItem.description) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_description',
        type: 'description',
        value: { from: historyItem.description.from, to: historyItem.description.to }
      })
    }
    if (historyItem.dueDate) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_dueDate',
        type: 'dueDate',
        value: { from: historyItem.dueDate.from, to: historyItem.dueDate.to }
      })
    }
    if (historyItem.externalId) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_externalId',
        type: 'externalId',
        value: { from: historyItem.externalId.from, to: historyItem.externalId.to }
      })
    }
    if (historyItem.finishedDate) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_finishedDate',
        type: 'finishedDate',
        value: { from: historyItem.finishedDate.from, to: historyItem.finishedDate.to }
      })
    }
    if (historyItem.priority) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_priority',
        type: 'priority',
        value: { from: historyItem.priority.from, to: historyItem.priority.to }
      })
    }
    if (historyItem.startDate) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_startDate',
        type: 'startDate',
        value: { from: historyItem.startDate.from, to: historyItem.startDate.to }
      })
    }
    if (historyItem.status) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_status',
        type: 'status',
        value: { from: historyItem.status.from, to: historyItem.status.to }
      })
    }
    if (historyItem.title) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_title',
        type: 'title',
        value: { from: historyItem.title.from, to: historyItem.title.to }
      })
    }
    if (historyItem.typeId) {
      historyArray.push({
        createdDate: createdDate,
        id: historyItem.id + '_typeId',
        type: 'typeId',
        value: { from: historyItem.typeId.from, to: historyItem.typeId.to }
      })
    }
  })
  return historyArray
}
function getCustomColor(historyType: string, value: string | null | undefined) {
  switch (historyType) {
    case 'priority':
      return value ? getPriorityColorLight(value) : undefined
    case 'status':
      return value ? getStatusColorLight(value) : undefined
    default:
      return undefined
  }
}
function getFormattedValue(historyType: string, value: string | null | undefined) {
  let formattedValue = value
  if (historyType === 'dueDate' || historyType === 'finishedDate' || historyType === 'startDate')
    formattedValue = formattedValue?.length
      ? new Date(formattedValue).toLocaleDateString()
      : t('general__text__none-variant')
  if (historyType === 'priority')
    formattedValue = formattedValue?.length
      ? translatedPriority(formattedValue)
      : t('general__text__unknown')
  if (historyType === 'status')
    formattedValue = formattedValue?.length
      ? translatedStatus(formattedValue)
      : t('general__text__unknown')
  if (historyType === 'categoryId') {
    if (!formattedValue?.length) return t('general__text__none')
    const categoryName = ticketCategories.value.find((category) => category.id === formattedValue)
      ?.name[locale.value]
    formattedValue = categoryName ?? t('general__text__unknown')
  }
  if (historyType === 'typeId') {
    if (!formattedValue?.length) return t('general__text__none')
    const typeName = ticketTypes.value.find((type) => type.id === formattedValue)?.name[
      locale.value
    ]
    formattedValue = typeName ?? t('general__text__unknown')
  }
  if (historyType === 'assignee' || historyType === 'description' || historyType === 'title') {
    if (!formattedValue?.length) return t('general__text__none')
  }
  if (historyType === 'externalId') {
    if (!formattedValue?.length) return t('general__text__none-variant')
  }
  return formattedValue ?? ''
}
function getTitle(historyType: string) {
  switch (historyType) {
    case 'assignee':
      return t('general__text__assignee') + ' ' + t('general__text__changed').toLowerCase()
    case 'categoryId':
      return t('general__text__category') + ' ' + t('general__text__changed').toLowerCase()
    case 'description':
      return t('general__text__description') + ' ' + t('general__text__updated-was').toLowerCase()
    case 'dueDate':
      return t('general__text__due-date') + ' ' + t('general__text__updated-was').toLowerCase()
    case 'externalId':
      return t('general__text__external-id') + ' ' + t('general__text__changed').toLowerCase()
    case 'finishedDate':
      return t('general__text__finished-date') + ' ' + t('general__text__updated-was').toLowerCase()
    case 'log':
      return t('general__text__log')
    case 'priority':
      return t('general__text__priority') + ' ' + t('general__text__changed').toLowerCase()
    case 'startDate':
      return t('general__text__start-date') + ' ' + t('general__text__updated-was').toLowerCase()
    case 'status':
      return t('general__text__status') + ' ' + t('general__text__changed').toLowerCase()
    case 'title':
      return t('general__data__ticket-name') + ' ' + t('general__text__changed').toLowerCase()
    case 'typeId':
      return t('general__text__type') + ' ' + t('general__text__changed').toLowerCase()
    default:
      return ''
  }
}
async function reloadHistory() {
  historyItems.value = []
  ticketCategories.value = []
  ticketTypes.value = []
  isLoadingHistory.value = true
  if (!selectedTicket.value) return
  const historyResponse: Page<TicketPatch> | undefined = await getTicketHistoryPage(
    selectedTicket.value.id,
    0,
    500
  )
  if (!historyResponse) return
  if (historyResponse.content.some((historyItem) => historyItem.categoryId || historyItem.typeId)) {
    // Categories request is necessary for both categoryId and typeId
    const categoryResponse = await getTicketCategories()
    if (categoryResponse?.length) ticketCategories.value = categoryResponse
    // The types request is only necessary if there is any typeId in the history
    if (
      ticketCategories.value.length &&
      historyResponse.content.some((historyItem) => historyItem.typeId)
    ) {
      const typesArray: TicketType[] = []
      ticketCategories.value.forEach(async (category) => {
        const typesResponse = await getTicketTypesForCategory(category.id)
        if (typesResponse?.length) typesArray.push(...typesResponse)
      })
      ticketTypes.value = typesArray
    }
  }
  const historyArray: TicketLogItem[] = generateHistoryItems(historyResponse.content)
  const commentsResponse = await getTicketCommentsPage(selectedTicket.value.id, 0, 500)
  if (commentsResponse?.content.length) {
    commentsResponse.content.forEach((comment) => {
      historyArray.push({
        createdDate: comment.createdDate,
        id: comment.id,
        type: 'log',
        value: { from: null, to: comment.comment }
      })
    })
  }
  historyArray.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
  historyItems.value = historyArray
  isLoadingHistory.value = false
}

onMounted(() => {
  reloadHistory()
})
</script>
<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';

.edit-ticket-content {
  &__log {
    &__body {
      align-items: center;
      display: flex;
      gap: 8px;
      min-height: 24px;
      &__arrow {
        color: variables.$color-greyscale-secondary;
        min-width: 24px;
      }
      &__description {
        height: unset;
        justify-content: center;
        &__text {
          @include font-mixins.desktop-regular-paragraph;
          color: variables.$color-greyscale-secondary;
          margin: 0;
          width: calc(50% - 20px);
        }
      }
      &__text {
        @include font-mixins.desktop-regular-paragraph;
        color: variables.$color-greyscale-secondary;
        margin: 0;
      }
    }
    &__heading {
      @include font-mixins.desktop-bold-h5;
      color: variables.$color-greyscale-content;
      margin: 0 0 16px;
    }
    &__title {
      display: flex;
      justify-content: space-between;
      p {
        @include font-mixins.desktop-bold-paragraph;
        color: variables.$color-greyscale-content;
        margin: 0;
      }
    }
  }
  &__scrollable {
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px; // To avoid complex checks for scrollbar visibility, this padding was added as a "good enough" fix
    @include mixins.scrollbar-vertical;
  }
}
.spinner {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding-bottom: 32px;
}
</style>
